import { notifyManager, InfiniteQueryObserver, MutationObserver, QueriesObserver, QueryObserver, replaceEqualDeep } from '@tanstack/query-core';
export * from '@tanstack/query-core';
import { batch, createContext, onMount, onCleanup, createComponent, useContext, createSignal, untrack, createMemo, createEffect, on } from 'solid-js';

notifyManager.setScheduler((cb) => cb());
notifyManager.setBatchNotifyFunction(batch);

const QueryClientContext = createContext();
const useQueryClient = (queryClient) => {
  if (queryClient) {
    return queryClient;
  }
  const client = useContext(QueryClientContext);
  if (!client) {
    throw new Error("No QueryClient set, use QueryClientProvider to set one");
  }
  return client;
};
const QueryClientProvider = (props) => {
  const client = props.client;
  onMount(() => {
    client.mount();
    onCleanup(() => client.unmount());
  });
  return createComponent(QueryClientContext.Provider, {
    value: client,
    get children() {
      return props.children;
    }
  });
};

const createStateObject = (obj) => {
  const state = {};
  for (const key in obj) {
    const [value, setValue] = createSignal(obj[key]);
    Object.defineProperty(state, key, {
      get: value,
      set: (next) => setValue(typeof next === "function" ? () => next : next)
    });
  }
  return state;
};

function createBaseQuery(options, Observer, queryClient) {
  return untrack(() => {
    const client = useQueryClient(queryClient);
    const defaultedOptions = createMemo(() => {
      return client.defaultQueryOptions(options(client));
    });
    const initialDefaultedOptions = defaultedOptions();
    const observer = new Observer(
      client,
      initialDefaultedOptions
    );
    const result = createStateObject(observer.getOptimisticResult(initialDefaultedOptions));
    createEffect(
      on(
        defaultedOptions,
        ($defaultedOptions) => {
          observer.setOptions($defaultedOptions);
        },
        { defer: true }
      )
    );
    onCleanup(
      observer.subscribe((next) => {
        notifyManager.schedule(() => Object.assign(result, next));
      })
    );
    observer.updateResult();
    return result;
  });
}

function createInfiniteQuery(options, queryClient) {
  return createBaseQuery(
    options,
    InfiniteQueryObserver,
    queryClient
  );
}

function createMutation(options, queryClient) {
  return untrack(() => {
    const client = useQueryClient(queryClient);
    const defaultedOptions = createMemo(() => {
      return client.defaultMutationOptions(options(client));
    });
    const initialDefaultedOptions = defaultedOptions();
    const observer = new MutationObserver(
      client,
      initialDefaultedOptions
    );
    const mutate = (variables, mutateOptions) => {
      observer.mutate(variables, mutateOptions).catch(noop);
    };
    const initialResult = observer.getCurrentResult();
    const result = createStateObject({
      ...initialResult,
      mutate,
      mutateAsync: initialResult.mutate
    });
    createEffect(
      on(
        defaultedOptions,
        ($defaultedOptions) => {
          observer.setOptions($defaultedOptions);
        },
        { defer: true }
      )
    );
    onCleanup(
      observer.subscribe((next) => {
        notifyManager.schedule(() => {
          Object.assign(result, {
            ...next,
            mutate,
            mutateAsync: result.mutate
          });
        });
      })
    );
    return result;
  });
}
function noop() {
}

function createQueries(queriesOptions, queryClient) {
  return untrack(() => {
    const client = useQueryClient(queryClient);
    const defaultedOptions = createMemo(() => {
      const $queriesOptions = queriesOptions(client);
      return {
        queries: $queriesOptions.queries.map((queryOptions) => {
          return client.defaultQueryOptions(queryOptions);
        }),
        combine: $queriesOptions.combine
      };
    });
    const initialDefaultedOptions = defaultedOptions();
    const observer = new QueriesObserver(
      client,
      initialDefaultedOptions.queries,
      initialDefaultedOptions
    );
    const [, getInitialResult] = observer.getOptimisticResult(
      initialDefaultedOptions.queries,
      initialDefaultedOptions.combine
    );
    const [state, setState] = createSignal(getInitialResult());
    createEffect(
      on(defaultedOptions, ($defaultedOptions) => {
        observer.setQueries(
          $defaultedOptions.queries,
          $defaultedOptions
        );
      })
    );
    onCleanup(
      observer.subscribe(() => {
        const $defaultedOptions = defaultedOptions();
        const [, getResult] = observer.getOptimisticResult(
          $defaultedOptions.queries,
          $defaultedOptions.combine
        );
        setState(() => getResult());
      })
    );
    return state;
  });
}

function queryOptions(options) {
  return options;
}
function createQuery(options, queryClient) {
  return createBaseQuery(options, QueryObserver, queryClient);
}

function useIsFetching(filters, queryClient) {
  return untrack(() => {
    const client = useQueryClient(queryClient);
    const queryCache = client.getQueryCache();
    const [mutations, setMutations] = createSignal(client.isFetching(filters?.()));
    onCleanup(
      queryCache.subscribe((_result) => {
        notifyManager.schedule(() => {
          setMutations(client.isFetching(filters?.()));
        });
      })
    );
    return mutations;
  });
}

function useIsMutating(filters, queryClient) {
  return untrack(() => {
    const client = useQueryClient(queryClient);
    const mutationCache = client.getMutationCache();
    const [mutations, setMutations] = createSignal(client.isMutating(filters?.()));
    onCleanup(
      mutationCache.subscribe((_result) => {
        notifyManager.schedule(() => {
          setMutations(client.isMutating(filters?.()));
        });
      })
    );
    return mutations;
  });
}

function getResult(mutationCache, options) {
  return mutationCache.findAll(options.filters).map(
    (mutation) => options.select ? options.select(mutation) : mutation.state
  );
}
function useMutationState(options = () => ({}), queryClient) {
  return untrack(() => {
    const client = useQueryClient(queryClient);
    const mutationCache = client.getMutationCache();
    const [result, setResult] = createSignal(getResult(mutationCache, options()));
    onCleanup(
      mutationCache.subscribe(() => {
        notifyManager.schedule(() => {
          setResult((result2) => replaceEqualDeep(result2, getResult(mutationCache, options())));
        });
      })
    );
    return result;
  });
}

export { QueryClientContext, QueryClientProvider, createInfiniteQuery, createMutation, createQueries, createQuery, queryOptions, useIsFetching, useIsMutating, useMutationState, useQueryClient };
//# sourceMappingURL=solid-query.js.map
